import React, { useState } from 'react';
import {
    Users,
    Phone,
    MessageSquare,
    LogOut,
    Edit,
    Trash2,
    Save,
    X,
    Plus,
} from 'lucide-react';

// Mock data
const ADMINS = [
    {
        id: 1,
        name: 'John Smith',
        email: 'john.smith@xyz.com',
        smsNumbers: ['+61412345678', '+61423456789', '+61434567890'],
        webcallNumbers: ['+61412345678', '+61445678901', '+61456789012'],
    },
    {
        id: 2,
        name: 'Sarah Johnson',
        email: 'sarah.johnson@xyz.com',
        smsNumbers: ['+61467890123', '+61478901234', '+61489012345'],
        webcallNumbers: ['+61467890123', '+61490123456', '+61501234567'],
    },
    {
        id: 3,
        name: 'Mike Chen',
        email: 'mike.chen@xyz.com',
        smsNumbers: ['+61512345678', '+61523456789', '+61534567890'],
        webcallNumbers: ['+61512345678', '+61545678901', '+61556789012'],
    },
];

const NORMAL_USERS = [
    { id: 4, name: 'Alice Brown', email: 'alice.brown@xyz.com' },
    { id: 5, name: 'Bob Wilson', email: 'bob.wilson@xyz.com' },
    { id: 6, name: 'Carol Davis', email: 'carol.davis@xyz.com' },
    { id: 7, name: 'David Miller', email: 'david.miller@xyz.com' },
    { id: 8, name: 'Emma Taylor', email: 'emma.taylor@xyz.com' },
    { id: 9, name: 'Frank Anderson', email: 'frank.anderson@xyz.com' },
    { id: 10, name: 'Grace Thomas', email: 'grace.thomas@xyz.com' },
    { id: 11, name: 'Henry Jackson', email: 'henry.jackson@xyz.com' },
    { id: 12, name: 'Iris White', email: 'iris.white@xyz.com' },
    { id: 13, name: 'Jack Harris', email: 'jack.harris@xyz.com' },
];

// Configuration Form Component
function ConfigurationForm({
    currentAdmin,
    onSave,
    onCancel,
    editingConfig = null,
    configurations = [],
    onOverrideRequest = null,
}) {
    const [selectedUser, setSelectedUser] = useState(
        editingConfig?.userId?.toString() || ''
    );
    const [selectedSmsNumbers, setSelectedSmsNumbers] = useState(
        editingConfig?.smsNumbers || []
    );
    const [selectedWebcallNumbers, setSelectedWebcallNumbers] = useState(
        editingConfig?.webcallNumbers || []
    );
    const [smsDefault, setSmsDefault] = useState(
        editingConfig?.smsDefault || ''
    );
    const [webcallDefault, setWebcallDefault] = useState(
        editingConfig?.webcallDefault || ''
    );
    const [showOverrideDialog, setShowOverrideDialog] = useState(false);
    const [conflictingAdmin, setConflictingAdmin] = useState(null);
    const [pendingUserId, setPendingUserId] = useState('');

    const handleUserSelection = (userId) => {
        if (!userId) {
            setSelectedUser('');
            return;
        }

        const existingConfig = configurations.find(
            (c) => c.userId === parseInt(userId)
        );

        if (
            existingConfig &&
            existingConfig.provisionedBy !== currentAdmin.id &&
            !editingConfig
        ) {
            // User is configured by another admin - show override dialog
            const adminWhoConfigured = ADMINS.find(
                (a) => a.id === existingConfig.provisionedBy
            );
            setConflictingAdmin(adminWhoConfigured);
            setPendingUserId(userId);
            setShowOverrideDialog(true);
            return;
        }

        // Safe to select this user
        setSelectedUser(userId);

        // If editing existing config, load the data
        if (
            existingConfig &&
            existingConfig.provisionedBy === currentAdmin.id
        ) {
            setSelectedSmsNumbers(existingConfig.smsNumbers || []);
            setSelectedWebcallNumbers(existingConfig.webcallNumbers || []);
            setSmsDefault(existingConfig.smsDefault || '');
            setWebcallDefault(existingConfig.webcallDefault || '');
        } else {
            // Reset for new configuration
            setSelectedSmsNumbers([]);
            setSelectedWebcallNumbers([]);
            setSmsDefault('');
            setWebcallDefault('');
        }
    };

    const handleOverrideConfirm = () => {
        // Call override callback to remove existing config
        if (onOverrideRequest) {
            onOverrideRequest(parseInt(pendingUserId));
        }

        // Now select the user and reset form
        setSelectedUser(pendingUserId);
        setSelectedSmsNumbers([]);
        setSelectedWebcallNumbers([]);
        setSmsDefault('');
        setWebcallDefault('');
        setShowOverrideDialog(false);
        setPendingUserId('');
        setConflictingAdmin(null);
    };

    const handleOverrideCancel = () => {
        setSelectedUser('');
        setShowOverrideDialog(false);
        setPendingUserId('');
        setConflictingAdmin(null);
    };

    const handleSmsNumberChange = (number, checked) => {
        if (checked) {
            setSelectedSmsNumbers((prev) => [...prev, number]);
        } else {
            setSelectedSmsNumbers((prev) => prev.filter((n) => n !== number));
            if (smsDefault === number) setSmsDefault('');
        }
    };

    const handleWebcallNumberChange = (number, checked) => {
        if (checked) {
            setSelectedWebcallNumbers((prev) => [...prev, number]);
        } else {
            setSelectedWebcallNumbers((prev) =>
                prev.filter((n) => n !== number)
            );
            if (webcallDefault === number) setWebcallDefault('');
        }
    };

    const handleSave = () => {
        if (!selectedUser) {
            alert('Please select a user');
            return;
        }
        if (
            selectedSmsNumbers.length === 0 &&
            selectedWebcallNumbers.length === 0
        ) {
            alert('Please select at least one SMS or Webcall number');
            return;
        }

        onSave({
            userId: parseInt(selectedUser),
            smsNumbers: selectedSmsNumbers,
            webcallNumbers: selectedWebcallNumbers,
            smsDefault:
                smsDefault ||
                (selectedSmsNumbers.length > 0 ? selectedSmsNumbers[0] : ''),
            webcallDefault:
                webcallDefault ||
                (selectedWebcallNumbers.length > 0
                    ? selectedWebcallNumbers[0]
                    : ''),
            provisionedBy: currentAdmin.id,
        });
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingConfig ? 'Edit Configuration' : 'Add New Configuration'}
            </h3>

            <div className="grid md:grid-cols-3 gap-6">
                {/* User Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select User
                    </label>
                    <select
                        value={selectedUser}
                        onChange={(e) => handleUserSelection(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={editingConfig !== null}
                    >
                        <option value="">Choose a user...</option>
                        {NORMAL_USERS.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.name} ({user.email})
                            </option>
                        ))}
                    </select>
                </div>

                {/* SMS Numbers */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        SMS Numbers
                    </label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-3">
                        {currentAdmin.smsNumbers.map((number) => (
                            <label key={number} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedSmsNumbers.includes(
                                        number
                                    )}
                                    onChange={(e) =>
                                        handleSmsNumberChange(
                                            number,
                                            e.target.checked
                                        )
                                    }
                                    className="mr-2 text-green-600 focus:ring-green-500"
                                />
                                <span className="text-sm">{number}</span>
                            </label>
                        ))}
                    </div>

                    {/* SMS Default Selection */}
                    {selectedSmsNumbers.length > 0 && (
                        <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Default SMS Number
                            </label>
                            <div className="space-y-1">
                                {selectedSmsNumbers.map((number) => (
                                    <label
                                        key={number}
                                        className="flex items-center"
                                    >
                                        <input
                                            type="radio"
                                            name="smsDefault"
                                            value={number}
                                            checked={smsDefault === number}
                                            onChange={(e) =>
                                                setSmsDefault(e.target.value)
                                            }
                                            className="mr-2 text-green-600 focus:ring-green-500"
                                        />
                                        <span className="text-xs">
                                            {number}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Webcall Numbers */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Webcall Numbers
                    </label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-3">
                        {currentAdmin.webcallNumbers.map((number) => (
                            <label key={number} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedWebcallNumbers.includes(
                                        number
                                    )}
                                    onChange={(e) =>
                                        handleWebcallNumberChange(
                                            number,
                                            e.target.checked
                                        )
                                    }
                                    className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm">{number}</span>
                            </label>
                        ))}
                    </div>

                    {/* Webcall Default Selection */}
                    {selectedWebcallNumbers.length > 0 && (
                        <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Default Webcall Number
                            </label>
                            <div className="space-y-1">
                                {selectedWebcallNumbers.map((number) => (
                                    <label
                                        key={number}
                                        className="flex items-center"
                                    >
                                        <input
                                            type="radio"
                                            name="webcallDefault"
                                            value={number}
                                            checked={webcallDefault === number}
                                            onChange={(e) =>
                                                setWebcallDefault(
                                                    e.target.value
                                                )
                                            }
                                            className="mr-2 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-xs">
                                            {number}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center"
                >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center"
                >
                    <Save className="h-4 w-4 mr-1" />
                    {editingConfig ? 'Update' : 'Save'} Configuration
                </button>
            </div>

            {/* Override Confirmation Dialog */}
            {showOverrideDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Configuration Override Required
                        </h3>
                        <p className="text-gray-600 mb-4">
                            The user{' '}
                            <strong>
                                {
                                    NORMAL_USERS.find(
                                        (u) => u.id === parseInt(pendingUserId)
                                    )?.name
                                }
                            </strong>{' '}
                            is already configured by:
                        </p>
                        <div className="bg-orange-50 p-3 rounded mb-4">
                            <p className="font-medium text-orange-800">
                                {conflictingAdmin?.name}
                            </p>
                            <p className="text-sm text-orange-600">
                                {conflictingAdmin?.email}
                            </p>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Do you want to overwrite their configuration and
                            assign this user to yourself?
                        </p>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleOverrideCancel}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
                            >
                                NO
                            </button>
                            <button
                                onClick={handleOverrideConfirm}
                                className="px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                                YES, Override
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function TestMock() {
    const [currentAdmin, setCurrentAdmin] = useState(null);
    const [loginEmail, setLoginEmail] = useState('');
    const [globalConfigurations, setGlobalConfigurations] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);

    const handleLogin = () => {
        const admin = ADMINS.find((a) => a.email === loginEmail);
        if (admin) {
            setCurrentAdmin(admin);
        } else {
            alert('Invalid admin email');
        }
    };

    const handleLogout = () => {
        setCurrentAdmin(null);
        setLoginEmail('');
    };

    const handleSaveConfiguration = (config) => {
        if (editingConfig) {
            setGlobalConfigurations((prev) =>
                prev.map((c) => (c.userId === config.userId ? config : c))
            );
            setEditingConfig(null);
        } else {
            setGlobalConfigurations((prev) => [...prev, config]);
        }
        setShowForm(false);
    };

    const handleOverrideRequest = (userId) => {
        setGlobalConfigurations((prev) =>
            prev.filter((c) => c.userId !== userId)
        );
    };

    const handleEditConfiguration = (config) => {
        if (config.provisionedBy !== currentAdmin.id) {
            const adminWhoConfigured = ADMINS.find(
                (a) => a.id === config.provisionedBy
            );
            const confirmed = window.confirm(
                `This user is configured by ${adminWhoConfigured?.name} (${adminWhoConfigured?.email}). Do you want to overwrite their configuration?`
            );

            if (!confirmed) {
                return;
            }

            setGlobalConfigurations((prev) =>
                prev.filter((c) => c.userId !== config.userId)
            );
            setEditingConfig(null);
        } else {
            setEditingConfig(config);
        }

        setShowForm(true);
    };

    const handleDeleteConfiguration = (userId) => {
        setGlobalConfigurations((prev) =>
            prev.filter((c) => c.userId !== userId)
        );
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingConfig(null);
    };

    const getUserById = (userId) => {
        return NORMAL_USERS.find((u) => u.id === userId);
    };

    if (!currentAdmin) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
                    <div className="text-center mb-8">
                        <Users className="mx-auto h-12 w-12 text-blue-600 mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900">
                            User Management
                        </h1>
                        <p className="text-gray-600">Admin Login</p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Admin Email
                            </label>
                            <input
                                type="email"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="admin@xyz.com"
                                onKeyDown={(e) =>
                                    e.key === 'Enter' && handleLogin()
                                }
                            />
                        </div>

                        <button
                            onClick={handleLogin}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
                        >
                            Login
                        </button>
                    </div>

                    <div className="mt-6 text-xs text-gray-500">
                        <p className="font-semibold mb-2">Available Admins:</p>
                        {ADMINS.map((admin) => (
                            <p key={admin.id}>• {admin.email}</p>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <Users className="h-8 w-8 text-blue-600 mr-3" />
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900">
                                    User Management
                                </h1>
                                <p className="text-sm text-gray-600">
                                    Logged in as: {currentAdmin.name}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={handleLogout}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        >
                            <LogOut className="h-4 w-4 mr-2" />
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Admin Numbers Summary */}
                <div className="bg-white rounded-lg shadow mb-6 p-4">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Your Available Numbers
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <MessageSquare className="h-4 w-4 mr-2 text-green-600" />
                                SMS Numbers ({currentAdmin.smsNumbers.length})
                            </h3>
                            <div className="text-sm text-gray-600">
                                {currentAdmin.smsNumbers.join(', ')}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Phone className="h-4 w-4 mr-2 text-blue-600" />
                                Webcall Numbers (
                                {currentAdmin.webcallNumbers.length})
                            </h3>
                            <div className="text-sm text-gray-600">
                                {currentAdmin.webcallNumbers.join(', ')}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Configuration Button */}
                {!showForm && (
                    <div className="mb-6">
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add New Configuration
                        </button>
                    </div>
                )}

                {/* Configuration Form */}
                {showForm && (
                    <ConfigurationForm
                        currentAdmin={currentAdmin}
                        onSave={handleSaveConfiguration}
                        onCancel={handleCancelForm}
                        editingConfig={editingConfig}
                        configurations={globalConfigurations}
                        onOverrideRequest={handleOverrideRequest}
                    />
                )}

                {/* All Configured Users List */}
                {globalConfigurations.length > 0 && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">
                                All Configured Users
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                All users with SMS and/or Webcall configurations
                                (visible to all admins)
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            User
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Configured By
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            SMS Configuration
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Webcall Configuration
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {globalConfigurations.map((config) => {
                                        const user = getUserById(config.userId);
                                        const configuringAdmin = ADMINS.find(
                                            (a) => a.id === config.provisionedBy
                                        );
                                        const isOwnConfig =
                                            config.provisionedBy ===
                                            currentAdmin.id;

                                        return (
                                            <tr
                                                key={config.userId}
                                                className={`hover:bg-gray-50 ${
                                                    !isOwnConfig
                                                        ? 'bg-yellow-50'
                                                        : ''
                                                }`}
                                            >
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <div className="font-medium text-gray-900">
                                                            {user?.name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {user?.email}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div
                                                        className={`text-sm ${
                                                            isOwnConfig
                                                                ? 'text-green-600 font-medium'
                                                                : 'text-orange-600'
                                                        }`}
                                                    >
                                                        <div className="font-medium">
                                                            {
                                                                configuringAdmin?.name
                                                            }
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {
                                                                configuringAdmin?.email
                                                            }
                                                        </div>
                                                        {!isOwnConfig && (
                                                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded mt-1 inline-block">
                                                                Other Admin
                                                            </span>
                                                        )}
                                                        {isOwnConfig && (
                                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded mt-1 inline-block">
                                                                Your Config
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {config.smsNumbers.length >
                                                    0 ? (
                                                        <div className="space-y-1">
                                                            {config.smsNumbers.map(
                                                                (number) => (
                                                                    <div
                                                                        key={
                                                                            number
                                                                        }
                                                                        className="flex items-center"
                                                                    >
                                                                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-2">
                                                                            {
                                                                                number
                                                                            }
                                                                        </span>
                                                                        {number ===
                                                                            config.smsDefault && (
                                                                            <span className="text-xs text-green-600 font-medium">
                                                                                (Default)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">
                                                            Not configured
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {config.webcallNumbers
                                                        .length > 0 ? (
                                                        <div className="space-y-1">
                                                            {config.webcallNumbers.map(
                                                                (number) => (
                                                                    <div
                                                                        key={
                                                                            number
                                                                        }
                                                                        className="flex items-center"
                                                                    >
                                                                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2">
                                                                            {
                                                                                number
                                                                            }
                                                                        </span>
                                                                        {number ===
                                                                            config.webcallDefault && (
                                                                            <span className="text-xs text-blue-600 font-medium">
                                                                                (Default)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">
                                                            Not configured
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() =>
                                                                handleEditConfiguration(
                                                                    config
                                                                )
                                                            }
                                                            className={`px-3 py-1 rounded text-sm flex items-center ${
                                                                isOwnConfig
                                                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                                    : 'bg-orange-600 text-white hover:bg-orange-700'
                                                            }`}
                                                        >
                                                            <Edit className="h-3 w-3 mr-1" />
                                                            {isOwnConfig
                                                                ? 'Edit'
                                                                : 'Override'}
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleDeleteConfiguration(
                                                                    config.userId
                                                                )
                                                            }
                                                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center"
                                                        >
                                                            <Trash2 className="h-3 w-3 mr-1" />
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {globalConfigurations.length === 0 && !showForm && (
                    <div className="text-center py-12">
                        <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No Configurations Yet
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Start by adding a new user configuration
                        </p>
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center mx-auto"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add First Configuration
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
